import AttendedSessionItem, { Props as AttendedSessionProps } from '@/components/AttendedSessionItem'
import Block from '@/components/Block'
import Button from '@/components/Button'
import GsuLoader from '@/components/GsuLoader'
import Layout, { Stack } from '@/components/Layout'
import NoSessionsFound from '@/components/NoSessionsFound'
import Text from '@/components/Text'
import { coreLayoutContainer, coreLayoutSection } from '@/constants/measured-scope'
import { AttendedSessionsPageSection, PageInfo } from '@/shared/api-generated-types'
import uniq from 'lodash/uniq'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

export interface Props {
  items: AttendedSessionsPageSection['items']
  onLoadMore?: () => void
  pageInfo?: Partial<PageInfo>
  fetching?: boolean
}

const AttendedSessionsBlock = ({ items, onLoadMore, pageInfo, fetching }: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const canShowLoadMore = onLoadMore && pageInfo?.hasNextPage // 'Load more' is opt-in, by providing the callback. Also checks there's a new page to load first.
  const [cards, setCards] = useState<Array<React.ReactNode | null>>([])

  useEffect(() => {
    const newItems = items
      ? items.map((item) => {
          return (
            <AttendedSessionItem
              key={`session_${item.id}`}
              {...(item as AttendedSessionProps)}
              livenessType={item.livenessType}
            />
          )
        })
      : []

    setCards(() => {
      if (pageInfo?.nextPageCursor !== undefined) {
        return uniq([...cards, newItems])
      }
      return cards
    })
  }, [pageInfo?.nextPageCursor])

  return (
    <Block className={coreLayoutSection}>
      <Stack x className={coreLayoutContainer} justifyContent="center">
        <Stack y>
          <Text size="xs" weight="lg" responsiveSize={{ sm: 'md', lg: 'lg' }} className="pt-0">
            {t('nav.classNotes')}
          </Text>
          {items.length > 0 ? (
            <div>{cards}</div>
          ) : (
            <Layout>
              <NoSessionsFound />
            </Layout>
          )}
        </Stack>
      </Stack>

      {canShowLoadMore && (
        <div className="pt ta-c">
          <Button
            label={
              fetching ? (
                <>
                  <GsuLoader size={18} className="mr-sm" />
                  Loading...
                </>
              ) : (
                'Load more'
              )
            }
            size="lg"
            onClick={!fetching ? onLoadMore : undefined}
            component="button"
            disabled={fetching}
            theme="noBackground"
          />
        </div>
      )}
    </Block>
  )
}

export default AttendedSessionsBlock
